import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { difference } from "lodash-es";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerTopBar,
  PersistentDrawer,
  SwipeableDrawer,
} from "@/components/Drawer";
import { useHotelBookingContext } from "@/features/hotels/contexts/HotelBookingContext";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { calculateBookingPrice } from "@/features/hotels/utils/calculateBookingPrice";
import { TravellerFormDrawer } from "@/features/travellers/components/TravellerFormDrawer";
import { TravellerSelectionStatusDrawer } from "@/features/travellers/components/TravellerSelectionStatusDrawer";
import { TravellersList } from "@/features/travellers/components/TravellersList";
import { TravellersSelection } from "@/features/travellers/components/TravellersSelection";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import type {
  Traveller,
  TravellerAgeGroup,
} from "@/features/travellers/types/types";
import {
  getTravellerAgeAtTravelDate,
  getTravellerTypeAtTravelDate,
} from "@/features/travellers/utils/ageValidation";
import {
  getTravelerTypeOfAge,
  travelerTypeToTypeCode,
} from "@/features/travellers/utils/transformTraveler";
import { HotelTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/travellers",
)({
  component: RouteComponent,
  staticData: {
    drawer: true,
  },
});

function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();
  const params = Route.useParams();

  const { data: hotelData } = useHotelDataContext();
  const { travellerSlots, selectedTravellers, selectTraveller } =
    useTravellersSelectionContext();
  const { data: booking } = useHotelBookingContext();
  const { searchData } = useHotelSearchDataContext();

  const handleContinue = () => {
    if (!booking) {
      return;
    }

    const totalPrice = calculateBookingPrice(booking);

    rudderAnalytics.track(
      HotelTrackingEvents.USER_PROCEED_AFTER_ROOM_SELECTION,
      {
        bookingType: "Hotel",
        selected_offer_id: booking.offerId,
        price: totalPrice.value,
      },
    );

    navigate({ to: "/hotels/$id/summary" });
  };

  const [selectedSlot, setSelectedSlot] = useState<{
    ageGroup: TravellerAgeGroup;
    index: number;
  } | null>(null);

  const travelDate = useMemo(
    () => (searchData ? new Date(searchData.dates.checkIn) : null),
    [searchData],
  );

  if (!searchData || !travelDate || !hotelData) {
    Sentry.captureMessage(
      "User tried to access hotel travelers page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/$id/details" params={params} />;
  }

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  const childrenAges = searchData.rooms.flatMap((room) => room.childrenAges);

  return (
    <div className="px-4">
      <h2 className="text-xl font-medium text-text-title">
        {intl.formatMessage({
          defaultMessage: "Enter traveller details",
          description: "Page heading for the trip travellers selection page",
        })}
      </h2>
      <p className="mb-4 text-sm text-text-tertiary">
        {intl.formatMessage({
          defaultMessage: "Tap each traveller below to enter their information",
          description:
            "Instruction to tap each traveller card to enter their information",
        })}
      </p>

      <TravellersSelection
        onSlotClick={(ageGroup, index) => {
          setSelectedSlot({ ageGroup, index });
        }}
      />

      {selectedSlot ? (
        <TravellerSlotDrawer
          selectedSlot={selectedSlot}
          travelDate={travelDate}
          childrenAges={childrenAges}
          onTravellerSelected={(travellerId, slotIndex) => {
            selectTraveller(slotIndex, travellerId);
          }}
          onClose={() => setSelectedSlot(null)}
        />
      ) : null}

      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <PersistentDrawer>
        <Button
          fullWidth
          disabled={!allTravellersSelected}
          variant="contained"
          onClick={handleContinue}
        >
          {intl.formatMessage({
            defaultMessage: "Continue",
            description: "Button to proceed to the next hotel booking step",
          })}
        </Button>
      </PersistentDrawer>
    </div>
  );
}

type TravellerSlotDrawerProps = {
  selectedSlot: { ageGroup: TravellerAgeGroup; index: number };
  travelDate: Date;
  childrenAges: (number | null)[];
  onTravellerSelected: (travellerId: string, slotIndex: number) => void;
  onClose: () => void;
};

function TravellerSlotDrawer({
  selectedSlot,
  travelDate,
  childrenAges,
  onTravellerSelected,
  onClose,
}: TravellerSlotDrawerProps) {
  const intl = useIntl();

  const { data: travellers } = useQuery(travellersQueryOptions());
  const { travellerSlots, selectedTravellers } =
    useTravellersSelectionContext();

  const selectedChildrenAges = useMemo(() => {
    const selectedChildrenIds = travellerSlots.CHD.filter(Boolean);
    return selectedChildrenIds.map((id) => {
      const foundTraveller = travellers?.find(
        (traveller) => traveller.id === id,
      );
      if (!foundTraveller || !travelDate) return null;
      return getTravellerAgeAtTravelDate(foundTraveller.birthDate, travelDate);
    });
  }, [travelDate, travellerSlots.CHD, travellers]);

  const isSelectionDisabled = (traveller: Traveller) =>
    selectedTravellers.some((t) => t.id === traveller.id) ||
    // for children, we only allow selecting travellers with ages that were selected in the search
    (["child", "infant"].includes(
      getTravellerTypeAtTravelDate(traveller.birthDate, travelDate),
    )
      ? !difference(childrenAges, selectedChildrenAges).includes(
          getTravellerAgeAtTravelDate(traveller.birthDate, travelDate),
        )
      : false);

  const slotTitle = intl.formatMessage(
    {
      defaultMessage: "Add traveller for {type}",
      description: "Prefix label when assigning a traveller to a booking slot",
    },
    {
      type: intl.formatMessage(
        {
          defaultMessage:
            "{type, select, ADT {Adult} CHD {Child} INF {Infant} other {Traveller}} {number}",
          description:
            "Numbered traveller slot label (e.g. 'Adult 1', 'Child 2')",
        },
        {
          type: selectedSlot.ageGroup,
          number: selectedSlot.index + 1,
        },
      ),
    },
  );

  if (travellers?.length) {
    return (
      <SwipeableDrawer
        hasFullHeight
        open
        onClose={onClose}
        backgroundColor="background"
      >
        <DrawerTopBar onCloseClick={onClose} title={slotTitle} />
        <DrawerContent>
          <TravellersList
            travellers={(travellers || []).filter(
              (traveller) =>
                travelerTypeToTypeCode(
                  getTravellerTypeAtTravelDate(traveller.birthDate, travelDate),
                ) === selectedSlot.ageGroup,
            )}
            travelDate={travelDate}
            selectedTravellerId={
              travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
            }
            isSelectionDisabled={isSelectionDisabled}
            onTravellerSelected={(travellerId) => {
              onTravellerSelected(travellerId, selectedSlot.index);
              onClose();
            }}
          />
        </DrawerContent>
      </SwipeableDrawer>
    );
  }

  const remainingChildAges = difference(childrenAges, selectedChildrenAges);
  const requiredAges =
    selectedSlot.ageGroup !== "ADT"
      ? remainingChildAges.filter(
          (age): age is number =>
            age !== null &&
            travelerTypeToTypeCode(getTravelerTypeOfAge(age)) ===
              selectedSlot.ageGroup,
        )
      : undefined;

  return (
    <TravellerFormDrawer
      open
      traveller={null}
      title={slotTitle}
      ageGroupConstraint={{
        ageGroup: selectedSlot.ageGroup,
        travelDate,
        requiredAges,
      }}
      onSubmitSuccess={(traveller) => {
        onTravellerSelected(traveller.id, selectedSlot.index);
        onClose();
      }}
      onClose={onClose}
    />
  );
}
